import { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Chip,
  useTheme,
  Card,
  CardContent,
  Avatar,
  alpha,
  LinearProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack,
  // Modern Widget Icons
  Favorite as HeartIcon,
  Terrain as ElevationIcon,
  RotateRight as CadenceIcon,
  Bolt as PowerIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Timer as TimerIcon,
  Repeat as RepeatIcon,
  Assessment as DataQualityIcon,
  Info as InfoIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as SuggestionIcon,
  BarChart as ChartIcon,
} from '@mui/icons-material';
import type { WorkoutSegmentResponse, WorkoutSegmentDetailResponse } from '../../../../store/api/models/workoutModels';
import { useGetSegmentDetailQuery, useGetHeartRateZonesQuery, useGetPaceZonesQuery } from '../../../../store/api/baseApi';
import { ZoneDistributionChart } from '../ZoneDistributionChart';
import { WorkoutAwareChart } from '../WorkoutAwareChart';
import type { ActivityStreamResponse } from '../../../../types';
import {
  getIntensityScoreInterpretation,
  getDeviationInterpretation,
  formatDeviation,
  getDeviationUnit,
} from '../../utils/intensityInterpretation';

interface SegmentDetailViewProps {
  segment: WorkoutSegmentResponse;
  onBack: () => void;
  sportType?: string;
  segmentDetail?: WorkoutSegmentDetailResponse; // Optional: if provided, skip fetching
}



// Format duration from seconds to HH:MM:SS or MM:SS
const formatDuration = (seconds?: number | null): string => {
  if (!seconds) return '--:--';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Format time axis (seconds to MM:SS) - rounds to whole seconds
const formatTimeAxis = (seconds: number): string => {
  const roundedSeconds = Math.round(seconds);
  const minutes = Math.floor(roundedSeconds / 60);
  const secs = roundedSeconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Format distance axis (meters to km with 1 decimal place)
const formatDistanceAxis = (meters: number): string => {
  return (meters / 1000).toFixed(1);
};

// Format pace from seconds to min:sec
const formatPaceValue = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Format pace from seconds per km to min:sec/km
const formatPace = (secondsPerKm?: number | null): string => {
  if (!secondsPerKm) return '--:--';
  return formatPaceValue(secondsPerKm);
};

// Format distance from meters to km
const formatDistance = (meters?: number | null): string => {
  if (!meters) return '--';
  return (meters / 1000).toFixed(2);
};

// Get color for compliance score
const getComplianceColor = (score?: number | null): string => {
  if (!score) return '#9e9e9e'; // Gray for no data
  if (score >= 0.8) return '#66bb6a'; // Green for great (>= 80%)
  if (score >= 0.6) return '#ffa726'; // Orange for good (60-80%)
  return '#ef5350'; // Red for needs improvement (< 60%)
};

// Get label for compliance score
const getComplianceLabel = (score?: number | null): string => {
  if (!score) return 'No Data';
  if (score >= 0.8) return 'Great';
  if (score >= 0.6) return 'Good';
  return 'Needs Improvement';
};
// Convert m/s to km/h (for rides)
const convertToKmh = (mps: number | null): number | null => {
  if (mps === null || mps === undefined) return null;
  return mps * 3.6;
};

// Convert m/s to min/km (for runs) - returns seconds per km
const convertToMinPerKm = (mps: number | null): number | null => {
  if (mps === null || mps === undefined || mps === 0) return null;
  return 1000 / mps;
};

// Modern Widget Component
interface MetricWidgetProps {
  title: string;
  value: string | number;
  unit?: string;
  subtitle?: string | React.ReactNode;
  icon: any;
  // Cast color to a union of valid palette keys
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  bgColor?: string;
}

const MetricWidget = ({
  title,
  value,
  unit,
  subtitle,
  icon: Icon,
  color = 'primary',
  bgColor
}: MetricWidgetProps) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)',
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
            {title}
          </Typography>
          <Avatar
            sx={{
              bgcolor: bgColor ? alpha(bgColor, 0.15) : alpha(theme.palette[color].main, 0.15),
              color: bgColor || theme.palette[color].main,
              width: 32,
              height: 32
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Avatar>
        </Stack>

        <Box>
          <Typography variant="h5" fontWeight="bold" color="text.primary" lineHeight={1.2}>
            {value}
            {unit && <Typography component="span" variant="body2" color="text.secondary" ml={0.5}>{unit}</Typography>}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const SegmentDetailView = ({ segment, onBack, sportType = 'run', segmentDetail: providedSegmentDetail }: SegmentDetailViewProps) => {
  const isRun = sportType.toLowerCase().includes('run');
  const [showComplianceAnalysis, setShowComplianceAnalysis] = useState(false);


  // Fetch detailed segment data with streams (only if not provided)
  const { data: fetchedSegmentDetail, isLoading, error } = useGetSegmentDetailQuery(
    {
      segmentId: segment.id,
      sampling_rate: 2,
    },
    {
      skip: !!providedSegmentDetail, // Skip fetching if data is already provided
    }
  );

  // Use provided data if available, otherwise use fetched data
  const segmentDetail = providedSegmentDetail || fetchedSegmentDetail;

  // Fetch user's heart rate and pace zones for zone distribution visualization
  const { data: hrZonesData } = useGetHeartRateZonesQuery();
  const { data: paceZonesData } = useGetPaceZonesQuery();

  // Transform zones to match ZoneDistributionChart expected format
  const heartRateZones = useMemo(() => {
    if (!hrZonesData?.zones) return [];
    return hrZonesData.zones.map(zone => ({
      zone: zone.zone,
      min_bpm: zone.min_hr,
      max_bpm: zone.max_hr,
      name: zone.name,
      description: '',
    }));
  }, [hrZonesData]);

  const paceZones = useMemo(() => {
    if (!paceZonesData?.zones) return [];
    return paceZonesData.zones.map(zone => ({
      zone: zone.zone,
      min_pace: zone.min_pace,
      max_pace: zone.max_pace,
      name: zone.name,
      description: '',
    }));
  }, [paceZonesData]);

  // Convert SegmentStreamData to ActivityStreamResponse format
  const chartStreams = useMemo<ActivityStreamResponse | null>(() => {
    if (!segmentDetail?.streams?.time) return null;
    return {
      time: segmentDetail.streams.time,
      distance: segmentDetail.streams.distance || [],
      latlng: segmentDetail.streams.latlng || [],
      altitude: segmentDetail.streams.altitude || [],
      velocity_smooth: segmentDetail.streams.velocity_smooth || [],
      heartrate: segmentDetail.streams.heartrate || null,
      cadence: segmentDetail.streams.cadence || null,
      watts: segmentDetail.streams.watts || null,
      temp: null,
      moving: [],
      grade_smooth: [],
    };
  }, [segmentDetail]);

  // Calculate percentages for target zones
  const targetZonesTotal = useMemo(() => {
    if (!segmentDetail) return 0;
    return (segmentDetail.time_in_target_seconds || 0) +
      (segmentDetail.time_above_target_seconds || 0) +
      (segmentDetail.time_below_target_seconds || 0);
  }, [segmentDetail]);

  const getTargetPct = (seconds?: number | null) => {
    if (!seconds || !targetZonesTotal) return 0;
    return (seconds / targetZonesTotal) * 100;
  };

  const inTargetPct = getTargetPct(segmentDetail?.time_in_target_seconds);
  const aboveTargetPct = getTargetPct(segmentDetail?.time_above_target_seconds);
  const belowTargetPct = getTargetPct(segmentDetail?.time_below_target_seconds);

  // Transform zone distribution data from API format to chart format
  const zoneMetrics = useMemo(() => {
    if (!segmentDetail) return null;

    const transformZoneDistribution = (
      zoneDistribution: { [zone: string]: number } | null | undefined,
      totalSeconds: number
    ) => {
      if (!zoneDistribution) return null;

      return Object.entries(zoneDistribution)
        .map(([zone, percentage]) => ({
          zone: parseInt(zone),
          percentage: percentage * 100, // Convert from 0-1 to 0-100
          time_seconds: percentage * totalSeconds,
        }))
        .filter(z => z.time_seconds > 0); // Only include zones with time spent
    };

    const totalDuration = segment.duration_seconds || 0;

    return {
      hr_zones: transformZoneDistribution(segmentDetail.hr_zone_distribution, totalDuration),
      pace_zones: transformZoneDistribution(segmentDetail.pace_zone_distribution, totalDuration),
      power_zones: null, // Power zone distribution not available in current API
    };
  }, [segmentDetail, segment.duration_seconds]);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" component="h2">
          {segment.segment_name ? segment.segment_name.charAt(0).toUpperCase() + segment.segment_name.slice(1) : `Segment ${segment.segment_index + 1}`}
        </Typography>
      </Box>



      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load segment stream data. Please try again.
        </Alert>
      )}

      {/* Performance Summary - Modernized with Widgets */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Summary
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          {/* Duration & Distance Widget */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricWidget
              title="Duration"
              value={formatDuration(segment.duration_seconds || 0)}
              subtitle={`Distance: ${formatDistance(segment.distance_meters || 0)} km`}
              icon={TimerIcon}
              color="primary"
            />
          </Grid>

          {/* Avg Pace/Speed Metrics Widget */}
          {segment.avg_pace_per_km && (
            <Grid item xs={12} sm={6} md={3}>
              <MetricWidget
                title="Avg Pace"
                value={formatPace(segment.avg_pace_per_km)}
                unit="/km"
                subtitle={
                  segment.pace_consistency_score !== null && segment.pace_consistency_score !== undefined
                    ? `Consistency: ${(segment.pace_consistency_score * 100).toFixed(0)}%`
                    : ''
                }
                icon={SpeedIcon}
                color="info"
              />
            </Grid>
          )}

          {/* Max Pace/Speed Widget */}
          {segment.max_speed_mps && (
            <Grid item xs={12} sm={6} md={3}>
              <MetricWidget
                title={isRun ? "Max Pace" : "Max Speed"}
                value={isRun ? formatPace(convertToMinPerKm(segment.max_speed_mps) || 0) : (convertToKmh(segment.max_speed_mps) || 0).toFixed(1)}
                unit={isRun ? "/km" : "km/h"}
                subtitle={isRun ? "Fastest" : "Top speed"}
                icon={SpeedIcon}
                color="info"
                bgColor="#42a5f5"
              />
            </Grid>
          )}

          {/* Heart Rate Metrics Widget */}
          {segment.avg_heartrate && (
            <Grid item xs={12} sm={6} md={3}>
              <MetricWidget
                title="Avg Heart Rate"
                value={segment.avg_heartrate.toFixed(0)}
                unit="bpm"
                subtitle={
                  <>
                    Min: {segment.min_heartrate?.toFixed(0)} • Max: {segment.max_heartrate?.toFixed(0)}
                  </>
                }
                icon={HeartIcon}
                color="error"
              />
            </Grid>
          )}

          {/* Elevation Gain Widget */}
          {(segment.elevation_gain_meters !== null && segment.elevation_gain_meters !== undefined) && (
            <Grid item xs={12} sm={6} md={3}>
              <MetricWidget
                title="Elevation Gain"
                value={segment.elevation_gain_meters > 0 ? `+${segment.elevation_gain_meters.toFixed(0)}` : segment.elevation_gain_meters.toFixed(0)}
                unit="m"
                subtitle={
                  segment.elevation_loss_meters !== null && segment.elevation_loss_meters !== undefined
                    ? `Loss: ${segment.elevation_loss_meters.toFixed(0)}m`
                    : ''
                }
                icon={ElevationIcon}
                color="success"
                bgColor="#66bb6a"
              />
            </Grid>
          )}

          {/* Cadence Widget */}
          {segment.avg_cadence && (
            <Grid item xs={12} sm={6} md={3}>
              <MetricWidget
                title="Avg Cadence"
                value={segment.avg_cadence.toFixed(0)}
                unit={isRun ? "spm" : "rpm"}
                subtitle={`Max: ${segment.max_cadence?.toFixed(0) || 0} ${isRun ? "spm" : "rpm"}`}
                icon={CadenceIcon}
                color="warning"
              />
            </Grid>
          )}

          {/* Power Widget */}
          {segment.avg_watts && (
            <Grid item xs={12} sm={6} md={3}>
              <MetricWidget
                title="Avg Power"
                value={segment.avg_watts.toFixed(0)}
                unit="W"
                subtitle={`Norm: ${segment.normalized_power?.toFixed(0) || 0}W • Max: ${segment.max_watts?.toFixed(0) || 0}W`}
                icon={PowerIcon}
                color="secondary"
              />
            </Grid>
          )}

          {/* Target Achievement Widget */}
          {segment.target_achieved !== null && segment.target_achieved !== undefined && (
            <Grid item xs={12} sm={6} md={3}>
              <MetricWidget
                title="Target Status"
                value={segment.target_achieved ? "Achieved" : "Missed"}
                subtitle={
                  segment.target_deviation_pct !== null && segment.target_deviation_pct !== undefined
                    ? `Deviation: ${(segment.target_deviation_pct * 100).toFixed(1)}%`
                    : ''
                }
                icon={segment.target_achieved ? CheckIcon : CancelIcon}
                color={segment.target_achieved ? "success" : "error"}
                bgColor={segment.target_achieved ? "#66bb6a" : "#ef5350"}
              />
            </Grid>
          )}

          {/* Set Information Widget (for interval workouts) */}
          {segment.set_rep !== null && segment.set_rep !== undefined && segment.set_total !== null && segment.set_total !== undefined && (
            <Grid item xs={12} sm={6} md={3}>
              <MetricWidget
                title="Set Progress"
                value={`${segment.set_rep} / ${segment.set_total}`}
                subtitle={`${segment.step_type || 'movement'}`}
                icon={RepeatIcon}
                color="primary"
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Workout-Aware Performance Charts */}
      {!isLoading && !error && chartStreams && segmentDetail && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <WorkoutAwareChart
            streams={chartStreams}
            workoutSegments={[segmentDetail]}
            isLoading={false}
            xAxisType="time"
            initialCollapsed={false}
            chartHeight={300}
            fullscreenChartHeight={400}
            sportType={sportType}
            showImmersiveButton={false}
          />
        </Paper>
      )}

      {/* No stream data message */}
      {!isLoading && !error && !chartStreams && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No detailed stream data available for this segment.
        </Alert>
      )}

      {/* Compliance Metrics - Card Wrapped 2x2 Grid + Toggleable Right Panel */}
      {!isLoading && !error && segmentDetail && (segmentDetail.target_compliance_score !== null || segmentDetail.intensity_compliance_pct !== null || segmentDetail.overall_intensity_score !== null) && (() => {
        // Get interpretations for metrics
        const intensityScoreInterpretation = getIntensityScoreInterpretation(segmentDetail.overall_intensity_score);
        const deviationInterpretations = getDeviationInterpretation(
          segmentDetail.target_intensity_focus,
          segmentDetail.avg_deviation_above,
          segmentDetail.avg_deviation_below
        );

        const hasSuggestions = intensityScoreInterpretation.message || deviationInterpretations.above || deviationInterpretations.below;
        const theme = useTheme();

        // Check if there are any left column metrics to display
        const hasLeftColumnMetrics =
          segmentDetail.overall_intensity_score !== null ||
          segmentDetail.intensity_compliance_pct !== null ||
          segmentDetail.avg_deviation_below !== null ||
          segmentDetail.avg_deviation_above !== null;

        // Get the compliance color for the header
        const complianceColor = getComplianceColor(segmentDetail.target_compliance_score);

        // Helper for Deviation Cards
        const DeviationCard = ({ title, value, unit, color, severity, icon: Icon, tooltip }: any) => (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2.5,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              bgcolor: alpha(color, 0.04),
              borderColor: alpha(color, 0.3),
              transition: 'transform 0.2s',
              // Force min-height to ensure grid consistency - matches right panel height
              minHeight: 222,
              '&:hover': {
                transform: 'translateY(-2px)',
                borderColor: color,
              }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {title}
              </Typography>
              <Tooltip title={tooltip} arrow placement="top">
                <InfoIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', '&:hover': { color: 'text.secondary' } }} />
              </Tooltip>
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ color: color }}>
                {value}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500} mt={0.5}>
                {severity}
              </Typography>
            </Box>
          </Paper>
        );

        return (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Workout Compliance
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3} alignItems="stretch">
              {/* LEFT COLUMN: 2x2 Grid - Only render if metrics exist */}
              {hasLeftColumnMetrics && (
                <Grid item xs={12} md={7}>
                  <Grid container spacing={2} sx={{ height: '100%' }}>
                    {/* 1. Intensity Control Score */}
                    {segmentDetail.overall_intensity_score !== null && (
                      <Grid item xs={12} sm={6}>
                        <DeviationCard
                          title="Intensity Control"
                          value={`${(segmentDetail.overall_intensity_score * 100).toFixed(0)}%`}
                          color={intensityScoreInterpretation.color}
                          severity={intensityScoreInterpretation.label}
                          tooltip="Comprehensive intensity score combining time in target AND deviation magnitude."
                        />
                      </Grid>
                    )}

                    {/* 2. Intensity (Time in Zone) */}
                    {segmentDetail.intensity_compliance_pct !== null && (
                      <Grid item xs={12} sm={6}>
                        <DeviationCard
                          title="Time in Zone"
                          value={`${segmentDetail.intensity_compliance_pct.toFixed(0)}%`}
                          color={getComplianceColor(segmentDetail.intensity_compliance_pct / 100)}
                          severity="Adherence"
                          tooltip="Percentage of time spent strictly within the programmed target intensity range."
                        />
                      </Grid>
                    )}

                    {/* 3. Below Target Deviation */}
                    {segmentDetail.avg_deviation_below !== null && (
                      <Grid item xs={12} sm={6}>
                        <DeviationCard
                          title="Below Target"
                          value={`-${formatDeviation(segmentDetail.avg_deviation_below, segmentDetail.target_intensity_focus)}`}
                          color={deviationInterpretations.below?.color || theme.palette.info.main}
                          severity={deviationInterpretations.below?.severity || 'Minimal'}
                          tooltip="Average deviation when you were not intense enough (below target zone)."
                        />
                      </Grid>
                    )}

                    {/* 4. Above Target Deviation */}
                    {segmentDetail.avg_deviation_above !== null && (
                      <Grid item xs={12} sm={6}>
                        <DeviationCard
                          title="Above Target"
                          value={`+${formatDeviation(segmentDetail.avg_deviation_above, segmentDetail.target_intensity_focus)}`}
                          color={deviationInterpretations.above?.color || theme.palette.warning.main}
                          severity={deviationInterpretations.above?.severity || 'Minimal'}
                          tooltip="Average deviation when you were too intense (above target zone)."
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              {/* RIGHT COLUMN: Toggleable Statistics/Analysis Panel */}
              <Grid item xs={12} md={hasLeftColumnMetrics ? 5 : 12}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    height: 461,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    borderColor: 'divider',
                  }}
                >
                  {/* Panel Header with Toggle */}
                  <Box
                    sx={{
                      p: 2,
                      px: 3,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: alpha(complianceColor, 0.15),
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {showComplianceAnalysis ? <SuggestionIcon sx={{ color: complianceColor }} fontSize="small" /> : <ChartIcon sx={{ color: complianceColor }} fontSize="small" />}
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: complianceColor }}>
                        {showComplianceAnalysis ? 'Analysis & Suggestions' : 'Consistency Overview'}
                      </Typography>
                    </Stack>

                    {hasSuggestions && (
                      <Tooltip title={showComplianceAnalysis ? "Show Statistics" : "Show Analysis"}>
                        <IconButton
                          size="small"
                          onClick={() => setShowComplianceAnalysis(!showComplianceAnalysis)}
                          sx={{
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          {showComplianceAnalysis ? <ChartIcon fontSize="small" /> : <SuggestionIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Panel Content Area */}
                  <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'auto' }}>
                    {showComplianceAnalysis ? (
                      // VIEW A: ANALYSIS & SUGGESTIONS
                      <Stack spacing={2} sx={{ width: '100%', height: '100%', justifyContent: 'flex-start' }}>
                        {intensityScoreInterpretation.message && (
                          <Alert severity={intensityScoreInterpretation.level === 'excellent' ? 'success' : intensityScoreInterpretation.level === 'good' ? 'info' : intensityScoreInterpretation.level === 'fair' ? 'warning' : 'error'} icon={false} sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Overall: {intensityScoreInterpretation.message}</Typography>
                            {intensityScoreInterpretation.description && <Typography variant="caption" display="block">{intensityScoreInterpretation.description}</Typography>}
                          </Alert>
                        )}
                        {deviationInterpretations.above && (
                          <Alert severity="warning" icon={false} sx={{ border: '1px solid', borderColor: 'warning.light' }}>
                            <Typography variant="subtitle2" fontWeight="bold">When too intense: {deviationInterpretations.above.message}</Typography>
                            <Typography variant="caption" display="block">{deviationInterpretations.above.recommendation}</Typography>
                          </Alert>
                        )}
                        {deviationInterpretations.below && (
                          <Alert severity="info" icon={false} sx={{ border: '1px solid', borderColor: 'info.light' }}>
                            <Typography variant="subtitle2" fontWeight="bold">When not intense enough: {deviationInterpretations.below.message}</Typography>
                            <Typography variant="caption" display="block">{deviationInterpretations.below.recommendation}</Typography>
                          </Alert>
                        )}
                        {!intensityScoreInterpretation.message && !deviationInterpretations.above && !deviationInterpretations.below && (
                          <Box textAlign="center" py={4}>
                            <Typography variant="body2" color="text.secondary">No major suggestions derived from this data.</Typography>
                          </Box>
                        )}
                      </Stack>
                    ) : (
                      // VIEW B: STATS (Consistency + Time Dist)
                      <Box sx={{ width: '100%' }}>
                        <Grid container spacing={hasLeftColumnMetrics ? 0 : 4} alignItems="center">
                          {/* Overall Consistency Score */}
                          {segmentDetail.target_compliance_score !== null && (
                            <Grid item xs={12} md={hasLeftColumnMetrics ? 12 : 4}>
                              <Box textAlign="center" mb={hasLeftColumnMetrics ? 3 : 0}>
                                <Box position="relative" display="inline-flex" justifyContent="center">
                                  <CircularProgress
                                    variant="determinate"
                                    value={100}
                                    size={100}
                                    thickness={3}
                                    sx={{ color: theme.palette.action.hover }}
                                  />
                                  <CircularProgress
                                    variant="determinate"
                                    value={segmentDetail.target_compliance_score * 100}
                                    size={100}
                                    thickness={3}
                                    sx={{
                                      color: complianceColor,
                                      '& .MuiCircularProgress-circle': {
                                        stroke: complianceColor,
                                      },
                                      position: 'absolute',
                                      left: 0,
                                      strokeLinecap: 'round',
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      top: 0,
                                      left: 0,
                                      bottom: 0,
                                      right: 0,
                                      position: 'absolute',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ lineHeight: 1 }}>
                                      {(segmentDetail.target_compliance_score * 100).toFixed(0)}
                                      <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>%</Typography>
                                    </Typography>
                                  </Box>
                                </Box>
                                <Typography variant="body2" color="text.secondary" fontWeight={500} mt={1}>
                                  Target Execution Score
                                </Typography>
                              </Box>
                            </Grid>
                          )}

                          {hasLeftColumnMetrics && <Grid item xs={12}><Divider sx={{ mb: 2.5 }} /></Grid>}

                          {/* Time Distribution Stack */}
                          <Grid item xs={12} md={hasLeftColumnMetrics ? 12 : 8}>
                            <Box>
                              <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" mb={2} display="block">
                                Time Distribution
                              </Typography>
                              <Stack spacing={2}>
                                {/* In Target */}
                                <Box>
                                  <Box display="flex" justifyContent="space-between" mb={0.5} alignItems="center">
                                    <Typography variant="caption" fontWeight={600} color="text.primary">In Target</Typography>
                                    <Typography variant="caption" color="text.secondary">{formatDuration(segmentDetail.time_in_target_seconds)}</Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={inTargetPct}
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: alpha(theme.palette.success.main, 0.1),
                                      '& .MuiLinearProgress-bar': { bgcolor: theme.palette.success.main, borderRadius: 3 }
                                    }}
                                  />
                                </Box>

                                {/* Above Target */}
                                <Box>
                                  <Box display="flex" justifyContent="space-between" mb={0.5} alignItems="center">
                                    <Typography variant="caption" fontWeight={600} color="text.primary">Above Target</Typography>
                                    <Typography variant="caption" color="text.secondary">{formatDuration(segmentDetail.time_above_target_seconds)}</Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={aboveTargetPct}
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: alpha(theme.palette.error.main, 0.1),
                                      '& .MuiLinearProgress-bar': { bgcolor: theme.palette.error.main, borderRadius: 3 }
                                    }}
                                  />
                                </Box>

                                {/* Below Target */}
                                <Box>
                                  <Box display="flex" justifyContent="space-between" mb={0.5} alignItems="center">
                                    <Typography variant="caption" fontWeight={600} color="text.primary">Below Target</Typography>
                                    <Typography variant="caption" color="text.secondary">{formatDuration(segmentDetail.time_below_target_seconds)}</Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={belowTargetPct}
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: alpha(theme.palette.info.main, 0.1),
                                      '& .MuiLinearProgress-bar': { bgcolor: theme.palette.info.main, borderRadius: 3 }
                                    }}
                                  />
                                </Box>
                              </Stack>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        );
      })()}

      {/* Zone Distribution Chart */}
      {!isLoading && !error && segmentDetail && chartStreams && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Zone Distribution
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <ZoneDistributionChart
            streams={chartStreams}
            zoneMetrics={zoneMetrics}
            heartRateZones={heartRateZones}
            paceZones={paceZones}
            isLoading={false}
          />
        </Paper>
      )}


    </Box >
  );
};